import { pipe, Option, Array as A } from 'effect';
import type { CellRange, Column, GridApi } from 'ag-grid-community';

import type { BankFeedTransactionRow } from 'domain/bankFeed/schemas';
import parseAmount from 'pages/company/BankFeedRoute/components/BankFeedGrid/BankFeedSumStatusPanel/parseAmount';

export type SelectionSummary = { sum: number; count: number };

export const isAmountColumn = (column: Column): boolean => {
  const { type } = column.getColDef();
  return Array.isArray(type) ? type.includes('amount') : type === 'amount';
};

// startRow/endRow are optional on CellRange; a range missing either contributes no rows.
// endRow may sit ABOVE startRow when the user dragged upwards — normalise the order.
export const rangeRowIndexes = (range: CellRange): ReadonlyArray<number> =>
  pipe(
    Option.all({ start: Option.fromNullable(range.startRow), end: Option.fromNullable(range.endRow) }),
    Option.map(({ start, end }) => {
      const from = Math.min(start.rowIndex, end.rowIndex);
      const to = Math.max(start.rowIndex, end.rowIndex);
      return A.makeBy(to - from + 1, (i) => from + i);
    }),
    Option.getOrElse((): number[] => []),
  );

// Sums every selected cell belonging to an `amount`-typed column. Cells that fail
// to parse (empty cell, garbage) are skipped and do not count toward `count` —
// so `count` is the number of *summed* cells, not of selected cells.
export const computeSelectionSummary = (api: GridApi<BankFeedTransactionRow>): SelectionSummary =>
  pipe(
    api.getCellRanges() ?? [],
    A.flatMap((range) =>
      pipe(
        rangeRowIndexes(range),
        A.flatMap((rowIndex) =>
          pipe(
            range.columns,
            A.filter(isAmountColumn),
            A.filterMap((column) =>
              pipe(
                Option.fromNullable(api.getDisplayedRowAtIndex(rowIndex)),
                Option.flatMap((rowNode) => parseAmount(api.getCellValue({ rowNode, colKey: column }))),
              ),
            ),
          ),
        ),
      ),
    ),
    (values) => ({ sum: A.reduce(values, 0, (acc, value) => acc + value), count: values.length }),
  );
